/** Narrows to a plain-object dict so property access is cast-free inside the implementation. */
const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object';

const isPlainObject = (value: unknown): value is Record<string, unknown> => isObject(value) && !Array.isArray(value);

/**
 * Overload signatures give callers a precise return type:
 * – `target: T`          → returns `T`
 * – `target: Partial<T>` → returns `Partial<T>`
 */
type MergeDeep = {
  <T extends object>(target: T, source: Partial<T>, isMergingArrays?: boolean): T;
  <T extends object>(target: Partial<T>, source: Partial<T>, isMergingArrays?: boolean): Partial<T>;
};

/**
 * Performs a deep merge of `source` object into `target` object.
 * Creates a new object and does not mutate either `target` or `source`.
 */
export const mergeDeep: MergeDeep = <T extends object>(
  target: T | Partial<T>,
  source: Partial<T>,
  isMergingArrays = false
): T | Partial<T> => {
  if (!isObject(target) || !isObject(source)) {
    return isObject(source) ? structuredClone(source) : source;
  }

  const output = structuredClone(target);

  for (const [key, sourceValue] of Object.entries(source)) {
    const targetValue = output[key];

    if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
      // Both arrays: concatenate or replace. Always clone to avoid shared references.
      // targetValue is already owned by `output` (structuredClone of target); only
      // sourceValue elements need cloning before they enter the result.
      output[key] = isMergingArrays ? targetValue.concat(structuredClone(sourceValue)) : structuredClone(sourceValue);
    } else if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
      // Both plain objects: recurse.
      output[key] = mergeDeep(targetValue, sourceValue, isMergingArrays);
    } else {
      // Primitive, or array/object type mismatch: source wins. Clone objects to prevent reference leaks.
      output[key] = isObject(sourceValue) ? structuredClone(sourceValue) : sourceValue;
    }
  }

  return output;
};
